import {
  type MarketplaceAnnouncementStrip,
  type MarketplaceHeroBanner,
  type MarketplaceShowcaseCard,
  type ProgramRewardOffer,
} from '@/core/domain/entities/Product';

export type CampaignPriorityLevel = 'critical' | 'high' | 'normal' | 'low';
export type HeroContentKind = 'campaign' | 'partner' | 'service' | 'generic';
export type AnnouncementSurface = 'homepage' | 'merchandising' | 'other';

const MERCHANDISING_ROUTE_PREFIXES = ['/category', '/collections', '/brands', '/search'];

interface HeroContext {
  isPartnerDomain: boolean;
  partnerKey: string;
}

export interface CampaignAnnouncement {
  id: string;
  message: string;
  detail?: string;
  ctaLabel?: string;
  mobileCtaLabel?: string;
  ctaUrl?: string;
  priority: CampaignPriorityLevel;
  dismissible: boolean;
}

const PRIORITY_BASE_SCORE: Record<CampaignPriorityLevel, number> = {
  critical: 400,
  high: 300,
  normal: 200,
  low: 100,
};

const PRIORITY_ORDER: Record<CampaignPriorityLevel, number> = {
  critical: 0,
  high: 1,
  normal: 2,
  low: 3,
};

const MOTITO_DEFAULT_HERO_SLUG = 'motito-default-service-banner';
const MOTITO_DEFAULT_HERO_ID = 'motito-default-service-banner';
const SHOWCASE_ANCHOR_TITLES = ['trending now', 'trending'];

function normalizeText(...parts: Array<string | undefined | null>): string {
  return parts
    .filter(Boolean)
    .join(' ')
    .trim()
    .toLowerCase();
}

function asUnknownRecord(value: unknown): Record<string, unknown> {
  return (value ?? {}) as Record<string, unknown>;
}

function stripInlineHtml(raw?: string): string {
  if (!raw) return '';
  return raw
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function campaignSignalText(banner: MarketplaceHeroBanner): string {
  return normalizeText(
    banner.badge_text,
    banner.title,
    banner.subtitle,
    banner.cta_label,
    banner.supporting_text,
    banner.trailing_text,
    banner.slug
  );
}

function normalizeCollectionLabel(value?: string): string {
  return normalizeText(value).replace(/[_-]+/g, ' ');
}

function getScopeSpecificityScore(
  scope: MarketplaceAnnouncementStrip['scope'],
  surface: AnnouncementSurface
): number {
  if (surface === 'homepage') {
    return scope === 'homepage' ? 0 : 1;
  }
  if (surface === 'merchandising') {
    return scope === 'merchandising' ? 0 : 1;
  }
  return scope === 'global' ? 0 : 1;
}

export function inferAnnouncementSurface(pathname: string | null | undefined): AnnouncementSurface {
  if (!pathname || pathname === '/') {
    return 'homepage';
  }

  if (
    MERCHANDISING_ROUTE_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
    )
  ) {
    return 'merchandising';
  }

  return 'other';
}

export function getCampaignPriority(
  banner: MarketplaceHeroBanner | MarketplaceShowcaseCard
): CampaignPriorityLevel {
  const source = asUnknownRecord(banner);
  const explicit = source.campaign_priority;
  if (typeof explicit === 'string') {
    const normalized = explicit.trim().toLowerCase();
    if (
      normalized === 'critical' ||
      normalized === 'high' ||
      normalized === 'normal' ||
      normalized === 'low'
    ) {
      return normalized;
    }
  }
  return 'normal';
}

export function inferHeroContentKind(banner: MarketplaceHeroBanner): HeroContentKind {
  const source = asUnknownRecord(banner);
  const explicitKind = source.content_kind;
  if (typeof explicitKind === 'string') {
    const normalized = explicitKind.trim().toLowerCase();
    if (
      normalized === 'campaign' ||
      normalized === 'partner' ||
      normalized === 'service' ||
      normalized === 'generic'
    ) {
      return normalized;
    }
  }
  return 'generic';
}

export function isCampaignHeroBanner(banner: MarketplaceHeroBanner): boolean {
  const source = asUnknownRecord(banner);
  if (source.promotional_campaign_id || source.program_campaign_id) {
    return true;
  }
  if (inferHeroContentKind(banner) === 'campaign') {
    return true;
  }
  return /(giveaway|campaign|raffle|draw|prize|win\s+a\s)/.test(campaignSignalText(banner));
}

function createMotitoDefaultServiceBanner(): MarketplaceHeroBanner {
  return {
    id: MOTITO_DEFAULT_HERO_ID,
    slug: MOTITO_DEFAULT_HERO_SLUG,
    title: 'Pay small small.',
    subtitle: 'Phones, Fridges, Laptops and more.',
    badge_text: 'KEEP MY PRICE',
    cta_label: 'Shop now',
    cta_url: '/',
    image_desktop: '/assets/images/sliderImage.png',
    image_mobile: '/assets/images/sliderImage.png',
    supporting_text: "Market prices change often. Yours won't.",
    trailing_text: '',
    content_kind: 'service',
    campaign_priority: 'normal',
    priority: 10_000,
    rotation_group: 'default',
  };
}

function getHeroRank(
  banner: MarketplaceHeroBanner,
  index: number,
  context: HeroContext
): number {
  const explicitKind = inferHeroContentKind(banner);
  const kind: HeroContentKind =
    explicitKind === 'generic' && isCampaignHeroBanner(banner)
      ? 'campaign'
      : explicitKind;
  const priority = getCampaignPriority(banner);
  const priorityScore =
    PRIORITY_BASE_SCORE[priority] +
    (Number.isFinite(banner.priority) ? banner.priority : 0);

  let rank = priorityScore;

  if (kind === 'campaign' && priority === 'critical') {
    rank += 100_000;
  } else if (!context.isPartnerDomain && kind === 'campaign' && priority === 'high') {
    rank += 60_000;
  } else if (context.isPartnerDomain && (kind === 'partner' || kind === 'service')) {
    rank += 50_000;
  } else if (!context.isPartnerDomain && kind === 'service') {
    rank += 40_000;
  }

  // Stable ordering for equal ranks.
  rank += Math.max(0, 1000 - index);
  return rank;
}

export function buildOrderedHeroBanners(
  banners: MarketplaceHeroBanner[],
  context: HeroContext
): MarketplaceHeroBanner[] {
  if (!banners.length) {
    return [];
  }

  const hasServiceLikeBanner = banners.some((banner) => {
    const kind = inferHeroContentKind(banner);
    return kind === 'service' || kind === 'partner';
  });

  const withFallback =
    !context.isPartnerDomain && !hasServiceLikeBanner
      ? [createMotitoDefaultServiceBanner(), ...banners]
      : banners;

  const ordered = withFallback
    .map((banner, index) => ({
      banner,
      rank: getHeroRank(banner, index, context),
    }))
    .sort((a, b) => b.rank - a.rank)
    .map((item) => item.banner);

  // Keep the service proposition first on default Motito domains.
  if (!context.isPartnerDomain) {
    const serviceIndex = ordered.findIndex((banner) => {
      const kind = inferHeroContentKind(banner);
      return kind === 'service' || kind === 'partner';
    });
    if (serviceIndex > 0) {
      const [serviceBanner] = ordered.splice(serviceIndex, 1);
      if (serviceBanner) {
        ordered.unshift(serviceBanner);
      }
    }
  }

  return ordered;
}

function buildTitleCtaKey(title?: string, ctaUrl?: string): string | null {
  const normalizedTitle = normalizeText(title);
  const normalizedCta = normalizeText(ctaUrl);
  if (!normalizedTitle && !normalizedCta) {
    return null;
  }
  return `title-cta:${normalizedTitle}|${normalizedCta}`;
}

function buildHeroCampaignKeys(banner: MarketplaceHeroBanner): string[] {
  const source = asUnknownRecord(banner);
  const keys: string[] = [];

  const promoId = source.promotional_campaign_id;
  if (promoId) keys.push(`promotional:${String(promoId)}`);

  const programId = source.program_campaign_id;
  if (programId) keys.push(`program:${String(programId)}`);

  if (banner.slug) keys.push(`slug:${banner.slug}`);

  const titleCtaKey = buildTitleCtaKey(banner.title, banner.cta_url);
  if (titleCtaKey) keys.push(titleCtaKey);

  if (!keys.length) {
    keys.push(`fallback:${normalizeText(banner.title, banner.cta_url)}`);
  }

  return keys;
}

function buildShowcaseCampaignKeys(card: MarketplaceShowcaseCard): string[] {
  const source = asUnknownRecord(card);
  const keys: string[] = [];

  const promoId = source.promotional_campaign_id;
  if (promoId) keys.push(`promotional:${String(promoId)}`);

  const programId = source.program_campaign_id;
  if (programId) keys.push(`program:${String(programId)}`);

  if (card.slug) keys.push(`slug:${card.slug}`);

  const titleCtaKey = buildTitleCtaKey(card.title, card.cta_url);
  if (titleCtaKey) keys.push(titleCtaKey);

  if (!keys.length) {
    keys.push(`fallback:${normalizeText(card.title, card.cta_url)}`);
  }

  return keys;
}

export function createHeroCampaignKeySet(banners: MarketplaceHeroBanner[]): Set<string> {
  return new Set(banners.flatMap((banner) => buildHeroCampaignKeys(banner)));
}

export function filterPublicShowcaseCards(
  cards: MarketplaceShowcaseCard[]
): MarketplaceShowcaseCard[] {
  return cards.filter((card) => card.kind !== 'campaign');
}

export function isIPhoneShowcaseCard(card: MarketplaceShowcaseCard): boolean {
  return card.kind === 'custom' && card.slug === 'iphone-renewed-showcase';
}

export function orderShowcaseCards(cards: MarketplaceShowcaseCard[]): MarketplaceShowcaseCard[] {
  const priority = (card: MarketplaceShowcaseCard) =>
    isIPhoneShowcaseCard(card) ? 0 : card.kind === 'pay4me' ? 1 : 2;
  return filterPublicShowcaseCards(cards).sort((a, b) =>
    priority(a) - priority(b) || a.sort_order - b.sort_order
  );
}

function isTrendingCollection(section: { key: string; title: string }): boolean {
  const normalizedTitle = normalizeCollectionLabel(section.title);
  const normalizedKey = normalizeCollectionLabel(section.key);
  return [...SHOWCASE_ANCHOR_TITLES].some((anchor) => {
    return (
      normalizedTitle === anchor ||
      normalizedTitle.includes(anchor) ||
      normalizedKey === anchor ||
      normalizedKey.includes(anchor)
    );
  });
}

export function getShowcaseInsertAfterIndex(
  sections: Array<{ key: string; title: string }>
): number {
  if (!sections.length) {
    return -1;
  }

  const trendingIndex = sections.findIndex((section) => isTrendingCollection(section));
  if (trendingIndex >= 0) {
    return trendingIndex;
  }

  return sections.length - 1;
}

export function isShowcaseCampaignDuplicated(
  card: MarketplaceShowcaseCard,
  heroCampaignKeys: Set<string>
): boolean {
  if (card.kind !== 'campaign') return false;
  return buildShowcaseCampaignKeys(card).some((key) => heroCampaignKeys.has(key));
}

export function getCampaignAnnouncement(
  strips: MarketplaceAnnouncementStrip[] = [],
  options: {
    surface?: AnnouncementSurface;
    isHomePage?: boolean;
    heroBanners?: MarketplaceHeroBanner[];
  } = {}
): CampaignAnnouncement | null {
  const surface = options.surface ?? (options.isHomePage === false ? 'other' : 'homepage');
  const isHomePage = surface === 'homepage';
  const eligibleStrips = strips.filter((strip) => {
    if (strip.scope === 'global') {
      return true;
    }
    if (strip.scope === 'homepage') {
      return isHomePage;
    }
    if (strip.scope === 'merchandising') {
      return surface === 'merchandising';
    }
    return false;
  });

  const eligibleWithMessage = eligibleStrips
    .map((strip) => ({
      strip,
      message: stripInlineHtml(strip.message),
    }))
    .filter((entry) => Boolean(entry.message));

  const stripCandidate = [...eligibleWithMessage].sort((a, b) => {
    // An urgent global notice must not be hidden by a routine page-specific promotion.
    const criticalA = a.strip.priority === 'critical';
    const criticalB = b.strip.priority === 'critical';
    if (criticalA !== criticalB) return criticalA ? -1 : 1;

    const specificityA = getScopeSpecificityScore(a.strip.scope, surface);
    const specificityB = getScopeSpecificityScore(b.strip.scope, surface);
    if (specificityA !== specificityB) {
      return specificityA - specificityB;
    }

    const priorityA = PRIORITY_ORDER[a.strip.priority] ?? PRIORITY_ORDER.normal;
    const priorityB = PRIORITY_ORDER[b.strip.priority] ?? PRIORITY_ORDER.normal;
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    return (a.strip.sort_order ?? 0) - (b.strip.sort_order ?? 0);
  })[0];

  if (stripCandidate) {
    const ctaLabel = stripInlineHtml(stripCandidate.strip.cta_label);
    const mobileCtaLabel =
      stripInlineHtml(stripCandidate.strip.mobile_cta_label) || undefined;

    return {
      id: stripCandidate.strip.id,
      message: stripCandidate.message,
      detail: stripInlineHtml(stripCandidate.strip.detail),
      ctaLabel: ctaLabel || undefined,
      mobileCtaLabel,
      ctaUrl: stripCandidate.strip.cta_url,
      priority: stripCandidate.strip.priority,
      dismissible: Boolean(stripCandidate.strip.dismissible),
    };
  }

  if (isHomePage && options.heroBanners?.length) {
    const topCampaignHero = [...options.heroBanners]
      .filter((banner) => isCampaignHeroBanner(banner))
      .sort((a, b) => {
        const priorityA = PRIORITY_ORDER[getCampaignPriority(a)] ?? PRIORITY_ORDER.normal;
        const priorityB = PRIORITY_ORDER[getCampaignPriority(b)] ?? PRIORITY_ORDER.normal;
        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }
        return (b.priority ?? 0) - (a.priority ?? 0);
      })[0];

    if (topCampaignHero) {
      const ctaLabel = stripInlineHtml(topCampaignHero.cta_label);
      const mobileCtaLabel = ctaLabel
        ? ctaLabel.replace(/^see\s+this\s+week'?s/i, 'See prize')
        : undefined;
      return {
        id: `hero-${topCampaignHero.id || topCampaignHero.slug}`,
        message: stripInlineHtml(topCampaignHero.title),
        detail: undefined,
        ctaLabel: ctaLabel || undefined,
        mobileCtaLabel,
        ctaUrl: topCampaignHero.cta_url,
        priority: getCampaignPriority(topCampaignHero),
        dismissible: false,
      };
    }
  }

  return null;
}

function parseIsoTimestamp(value?: string | null): number {
  if (!value) return Number.POSITIVE_INFINITY;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
}

export function getRewardOfferAnnouncement(
  offers: ProgramRewardOffer[] = []
): CampaignAnnouncement | null {
  if (!offers.length) return null;

  const eligible = offers
    .filter((offer) => {
      const offerState = normalizeText(offer.offer_state || offer.status);
      return (
        offerState === 'reserved' ||
        offerState === 'promised' ||
        offerState === 'pending_fulfillment' ||
        offerState === 'claimed'
      );
    })
    .sort((a, b) => {
      const expiresA = parseIsoTimestamp(a.expires_at);
      const expiresB = parseIsoTimestamp(b.expires_at);
      if (expiresA !== expiresB) {
        return expiresA - expiresB;
      }
      return parseIsoTimestamp(a.created_at) - parseIsoTimestamp(b.created_at);
    });

  const primary = eligible[0];
  if (!primary) return null;

  const message = stripInlineHtml(primary.headline);
  const detail = stripInlineHtml(primary.body);
  const ctaLabel = stripInlineHtml(primary.cta_label);

  return {
    id: `reward-offer-${primary.id}`,
    message: message || 'A reward is waiting for you',
    detail: detail || undefined,
    ctaLabel: ctaLabel || undefined,
    mobileCtaLabel: ctaLabel || undefined,
    ctaUrl: primary.landing_page_url || '/sign-up',
    priority: 'critical',
    dismissible: false,
  };
}
